import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";

interface ThemeContextProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
  setTheme: (dark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const THEME_STORAGE_KEY = "tema";

const readStoredTheme = (): boolean => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === "dark";
  } catch {
    return false;
  }
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [darkMode, setDarkMode] = useState(readStoredTheme);

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  // Pra controles com dois alvos explícitos (ex.: segmentado Claro/Escuro),
  // onde cada opção precisa fixar um estado, não só inverter o atual.
  const setTheme = (dark: boolean) => {
    setDarkMode(dark);
  };

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, darkMode ? "dark" : "light");
    } catch {
      // localStorage indisponível (modo privado, etc.) — tema só não persiste, app segue funcionando
    }
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
